/**
 * useChatHistory — persist last N AI chat sessions (query + result) in localStorage.
 * Lightweight, purely client-side. No backend calls. Credit-free re-read of past answers.
 */
import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "nutramilo_ai_chat_history";
const MAX_ENTRIES = 3;

/**
 * Security: AI chat content may contain personal health questions/answers.
 * Using sessionStorage (cleared when tab closes) instead of localStorage
 * minimizes XSS attack surface and avoids indefinite persistence.
 */
const storage = typeof window !== "undefined" ? window.sessionStorage : null;

function safeLoad() {
  try {
    const raw = storage?.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, MAX_ENTRIES) : [];
  } catch {
    return [];
  }
}

function safeSave(entries) {
  try {
    const trimmed = entries.slice(0, MAX_ENTRIES).map((e) => ({
      id: e.id,
      query: (e.query || "").slice(0, 800),
      created_at: e.created_at,
      result: e.result,
    }));
    storage?.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch {
    /* ignore quota */
  }
}

export function useChatHistory() {
  const [history, setHistory] = useState(() => safeLoad());

  useEffect(() => {
    safeSave(history);
  }, [history]);

  const addEntry = useCallback((query, result) => {
    if (!query || !result) return;
    const entry = {
      id: `chat_${Date.now()}`,
      query: String(query).trim(),
      created_at: new Date().toISOString(),
      result,
    };
    setHistory((prev) => [entry, ...prev].slice(0, MAX_ENTRIES));
  }, []);

  const removeEntry = useCallback((id) => {
    setHistory((prev) => prev.filter((e) => e.id !== id));
  }, []);

  const clearHistory = useCallback(() => setHistory([]), []);

  return { history, addEntry, removeEntry, clearHistory };
}
